<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useLocale } from "@/composables/useLocale";
import type { GateError } from "@/types/gate";

const props = defineProps<{ error: GateError }>();

const emit = defineEmits<{
  retry: [];
  openApp: [];
  resend: [];
  close: [];
}>();

const { t } = useI18n();
const { locale } = useLocale();
const headingRef = ref<HTMLHeadingElement | null>(null);

onMounted(() => {
  headingRef.value?.focus();
});

const copyKey = computed(() => `error.${props.error.code}`);

const heading = computed(() => t(`${copyKey.value}.heading`));

const body = computed(() => {
  if (props.error.code === "rate_limited") {
    return t("error.rate_limited.body", {
      seconds: props.error.retryAfterSeconds ?? 60,
    });
  }
  return t(`${copyKey.value}.body`);
});

const ctaLabel = computed(() => t(`${copyKey.value}.cta`));

type CtaAction = "open_app" | "retry" | "resend" | "close";

const ctaAction = computed<CtaAction>(() => {
  switch (props.error.code) {
    case "not_confirmed":
    case "event_full":
    case "event_ended":
    case "event_cancelled":
    case "event_not_found":
    case "account_not_found":
    case "subscription_required":
      // subscription_required is normally rendered by the dedicated paywall
      // screen; this case only fires when the event-detail fetch failed and
      // we fell back to the generic error screen.
      return "open_app";
    case "not_live_yet":
      return "close";
    case "otp_expired":
    case "otp_locked":
      return "resend";
    default:
      return "retry";
  }
});

const contextLine = computed(() => {
  const event = props.error.event;
  if (!event) return null;
  const date = new Date(event.scheduledAt);
  const when = Number.isNaN(date.getTime())
    ? event.scheduledAt
    : new Intl.DateTimeFormat(locale.value, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
  return `${event.title} - ${when}`;
});

function onClick(): void {
  switch (ctaAction.value) {
    case "open_app":
      emit("openApp");
      return;
    case "retry":
      emit("retry");
      return;
    case "resend":
      emit("resend");
      return;
    case "close":
      emit("close");
      return;
  }
}
</script>

<template>
  <section class="error-screen gate-card" aria-live="assertive" role="alert">
    <h2 ref="headingRef" class="gate-heading" tabindex="-1">{{ heading }}</h2>
    <p v-if="contextLine" class="error-screen__context">{{ contextLine }}</p>
    <p class="gate-subheading">{{ body }}</p>

    <button type="button" class="gate-button error-screen__cta" @click="onClick">
      {{ ctaLabel }}
    </button>
  </section>
</template>

<style scoped>
.error-screen {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  text-align: left;
}

.error-screen__context {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}

.error-screen__cta {
  margin-top: var(--space-3);
}
</style>
